import { DeviceProtocol } from '@hid/device/device_model'
import { MagnetDriver } from '../device'
import { mg63Driver } from '../huochairen'
import { fakeSocket } from '@hid/fakesocket/fakesocket'
import { delay, generateMessageID, printHex, transpose } from '@libs/utils/tools'
import { allKeysForDevice, HIDFromVk, VKFromHID, vkFromKK } from '@hid/define/define_fn'
import { deviceManager } from '@hid/device/device_manager'
import { KK, OutputMode, SetMagnetSenior, VirtualMG75V2C12, VK } from '@hid/define/define'
import { MagnetSeniorType } from '@libs/types/enum'
import { DataDeadZone, postMagnetKeyInfo, SetMagnetKey } from '@libs/types/response'
import { decodeMagKeyInfo, getHuochairenMatrixIndexToKKMap, huochairenMagMapReverse, huochairenMagnetModeConvert, MG63MagKeyInfo, MG63SetCmd, validateSOCDPairs } from '../data_conversion/mg63'
import { MG63_MAG_KEY_INFO_SIZE_LEGACY, MG63_MAG_KEY_INFO_SIZE_V118, MG63_MAX_VALID_TRAVEL } from '../data_conversion/mg63'
import { TravelPostData } from '@hid/common/types'

//火柴人方案磁轴设置
export class mg63Magnet extends MagnetDriver {
  private calibrationSate = false
  private calibrationTimer?: ReturnType<typeof setInterval>
  initPromise: Promise<void>
  private fetchPromise: Promise<MG63MagKeyInfo[]> | null = null
  private deadZoneProtocolV118Promise: Promise<boolean> | null = null
  constructor(
    private DeviceHID: DeviceProtocol,
    private parent: mg63Driver,
  ) {
    super()

    this.initPromise = this.init()

    // onReciveAny 监听由 BaseDriver.destroy() 统一通过 DeviceProtocol.clearReciveAnyHandlers() 清理，无需在此手动维护 disposer。
    this.DeviceHID.onReciveAny((data) => {
      if (data[0] == 0xa0) {
        if (this.calibrationSate) {
          this.handleCalibrationData(data)
        } else {
          this.handleTravelTestData(data)
        }
      }
    })
  }

  private travelData: TravelPostData[] = []
  private adcData: TravelPostData[] = []

  private TravelTestTimes = 0 // 控制频率
  private async handleTravelTestData(data: number[]) {
    this.TravelTestTimes++
    if (this.TravelTestTimes == 12) {
      const sendData: TravelPostData[] = []
      const sendData1: TravelPostData[] = []
      if (data[1] > 0) {
        sendData.push({
          Key: data[1],
          Value: data[4] * 256 + data[5],
        })
        sendData1.push({
          Key: data[1],
          Value: data[2] * 256 + data[3],
        })
        await this.sendMessage(223, sendData1)
        await this.sendMessage(224, sendData)
      } else {
        allKeysForDevice(deviceManager.deviceID).forEach((key) => {
          sendData.push({
            Key: key,
            Value: 0,
          })
          sendData1.push({
            Key: key,
            Value: 0,
          })
        })
        await this.sendMessage(223, sendData1)
        await this.sendMessage(224, sendData)
      }
      this.TravelTestTimes = 0
    }
  }

  private async handleCalibrationData(data: number[]) {
    const packets = this.splitIntoPackets(data, 7)
    if (packets[packets.length - 1].length < 6) {
      packets.pop()
    }

    for (const packet of packets) {
      // esc键 开始存数据
      if (packet[1] == 0x29) {
        this.setCalibrationData(packet)
      }
      if (this.travelData.length > 0 && this.adcData.length > 0) {
        this.setCalibrationData(packet)
      }

      // TODO:根据设备判断结束的数据，进行存数据
      const endCode = deviceManager.deviceID === VirtualMG75V2C12 ? 0x4b : 0x4c
      if (packet[1] == endCode) {
        this.setCalibrationData(packet)
        await this.sendMessage(223, this.adcData)
        await this.sendMessage(224, this.travelData)

        this.travelData = []
        this.adcData = []
      }
    }
  }

  private setCalibrationData(packet: number[]) {
    this.travelData.push({
      Key: packet[1],
      Value: packet[5] * 256 + packet[6],
    })
    this.adcData.push({
      Key: packet[1],
      Value: packet[3] * 256 + packet[4],
    })
  }

  private async setKeyTravelCalibrationMode(isStart: boolean): Promise<void> {
    const data = Array(64).fill(0)
    data[0] = MG63SetCmd.Calibration
    data[1] = isStart ? 1 : 0
    await this.DeviceHID.send(data)
  }

  async startCalibration(): Promise<void> {
    await this.initPromise
    await this.parent.musicIns.musicCapturePause()
    await this.sendEndTravels()
    this.calibrationSate = true
    await this.setKeyTravelCalibrationMode(true).catch((e) => {
      console.error('calibrationStart error', e)
    })
    if (this.calibrationTimer) clearInterval(this.calibrationTimer)
    const currentHID = this.DeviceHID.deviceHID
    const timer = setInterval(() => {
      if (this.calibrationSate === false || this.DeviceHID.deviceHID !== currentHID) {
        clearInterval(timer)
        if (this.calibrationTimer === timer) this.calibrationTimer = undefined
        return
      }
      this.setKeyTravelCalibrationMode(true).catch((e) => {
        console.warn('calibration heartbeat failed', e)
      })
    }, 10000) // 每10秒发送一次保持校准状态
    this.calibrationTimer = timer
  }

  async endCalibration(): Promise<void> {
    if (this.calibrationSate == false) {
      return
    }
    if (this.calibrationTimer) {
      clearInterval(this.calibrationTimer)
      this.calibrationTimer = undefined
    }
    await this.setKeyTravelCalibrationMode(false).catch((e) => {
      console.error('calibrationEnd error', e)
    })
    this.calibrationSate = false
    await this.parent.musicIns.musicCaptureContinue()
  }

  private sendMessage<T>(type: number, data: T): void {
    fakeSocket.onmessage({
      data: JSON.stringify({
        ID: generateMessageID(),
        Type: type,
        Data: data,
        Error: null,
      }),
    } as MessageEvent)
  }

  // 设置按键行程进入/退出行程测试模式
  private async setKeyTravelMode(isStart: boolean) {
    const data = Array(64).fill(0)
    data[0] = MG63SetCmd.TravelTest
    data[1] = isStart ? 1 : 0
    return await this.DeviceHID.send(data)
  }

  async startTravels(): Promise<void> {
    await this.initPromise
    await this.parent.musicIns.musicCapturePause()
    await this.setKeyTravelMode(true).catch((e) => {
      console.error('startTravels error', e)
    })
  }
  async endTravels(): Promise<void> {
    await this.sendEndTravels()
    await delay(50)
    await this.parent.musicIns.musicCaptureContinue()
  }

  private async sendEndTravels() {
    await this.setKeyTravelMode(false).catch((e) => {
      console.error('startTravels error', e)
    })
  }

  private setTrPSx(keys: SetMagnetKey[]) {
    const data = transpose([keys[0].KeyFunction, keys[1].KeyFunction, keys[2].KeyFunction, keys[3].KeyFunction])
    const result: [number, number, number, number] = [0, 0, 0, 0]

    // 协议：dskEventConfig 高/低四位“从低到高依次表示 HidCode0-3”，
    // 因此 Keys[0]↔HidCode0↔bit4/bit0，Keys[3]↔HidCode3↔bit7/bit3。
    const bitMap = [
      [4, 0], // i=0 → HidCode0：高位 bit4, 低位 bit0
      [5, 1], // i=1 → HidCode1：高位 bit5, 低位 bit1
      [6, 2], // i=2 → HidCode2：高位 bit6, 低位 bit2
      [7, 3], // i=3 → HidCode3：高位 bit7, 低位 bit3
    ]

    for (let rowIndex = 0; rowIndex < 4; rowIndex++) {
      const row = data[rowIndex]
      for (let colIndex = 0; colIndex < 4; colIndex++) {
        const v = row[colIndex]
        const [highBit, lowBit] = bitMap[colIndex]

        if (v === 1) {
          result[rowIndex] |= 1 << highBit // 只设高位
        } else if (v === 2 || v === 3) {
          result[rowIndex] |= (1 << highBit) | (1 << lowBit) // 高+低都设
        }
      }
    }
    // console.log(result)
    // console.log(`0b${result[0].toString(2)}, 0b${result[1].toString(2)}, 0b${result[2].toString(2)}, 0b${result[3].toString(2)}`)
    return result
  }

  private async setMagnetKey(data: number[]) {
    await this.parent.send(data)
    await delay(10)
  }

  private getKKFormVK(value: number): number {
    if (!value) {
      return 0
    }
    const data = HIDFromVk(value as VK)
    if (data) {
      return data
    }
    throw new Error('未找到对应的按键值')
  }
  private async setDKS(data: Map<KK, SetMagnetSenior>): Promise<void> {
    for (const [key, value] of data) {
      const pressTravel = value.Keys[0].Travel * 100
      const releaseTravel = value.Keys[1].Travel * 100

      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetDKS
      sendData[1] = key

      sendData[2] = pressTravel & 0xff
      sendData[3] = (pressTravel >> 8) & 0xff
      sendData[4] = releaseTravel & 0xff
      sendData[5] = (releaseTravel >> 8) & 0xff

      // 协议：byte6..9 依次为 HidCode0..3，UI Keys[0..3] 按自然顺序写入对应槽位。
      sendData[6] = this.getKKFormVK(value.Keys[0].KeyCode[0])
      sendData[7] = this.getKKFormVK(value.Keys[1].KeyCode[0])
      sendData[8] = this.getKKFormVK(value.Keys[2].KeyCode[0])
      sendData[9] = this.getKKFormVK(value.Keys[3].KeyCode[0])

      const points = this.setTrPSx([value.Keys[0] as SetMagnetKey, value.Keys[1] as SetMagnetKey, value.Keys[2] as SetMagnetKey, value.Keys[3] as SetMagnetKey])

      sendData[10] = points[0]
      sendData[11] = points[3]
      sendData[12] = points[1]
      sendData[13] = points[2]

      await this.setMagnetKey(sendData)
    }
  }

  private async setMT(data: Map<KK, SetMagnetSenior>) {
    for (const [key, value] of data) {
      const duration = value.MTDuration * 10
      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetMT
      sendData[1] = key
      sendData[2] = this.getKKFormVK(value.Keys[1].KeyCode[0]) // 单击
      sendData[3] = this.getKKFormVK(value.Keys[0].KeyCode[0]) // 长按
      sendData[4] = duration & 0xff
      sendData[5] = duration >> 8
      await this.setMagnetKey(sendData)
    }
  }
  private async setTGL(data: Map<KK, SetMagnetSenior>) {
    for (const [key, value] of data) {
      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetTGL
      sendData[1] = key
      sendData[2] = this.getKKFormVK(value.Keys[0].KeyCode[0])
      sendData[3] = 1000 & 0xff
      sendData[4] = 1000 >> 8
      sendData[5] = value.TGLMode == 5 ? 1 : 2
      await this.setMagnetKey(sendData)
    }
  }
  private async setMPT(data: Map<KK, SetMagnetSenior>) {
    for (const [key, value] of data) {
      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetMPT
      sendData[1] = key
      sendData[2] = this.getKKFormVK(value.Keys[0].KeyCode[0])
      sendData[3] = value.Keys[0].Travel & 0xff
      sendData[4] = (value.Keys[0].Travel >> 8) & 0xff
      sendData[5] = this.getKKFormVK(value.Keys[1].KeyCode[0])
      sendData[6] = value.Keys[1].Travel & 0xff
      sendData[7] = (value.Keys[1].Travel >> 8) & 0xff
      sendData[8] = this.getKKFormVK(value.Keys[2].KeyCode[0])
      sendData[9] = value.Keys[2].Travel & 0xff
      sendData[10] = (value.Keys[2].Travel >> 8) & 0xff
      await this.setMagnetKey(sendData)
    }
  }
  private async setEND(data: Map<KK, SetMagnetSenior>) {
    for (const [key, value] of data) {
      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetEnd
      sendData[1] = key
      sendData[2] = key
      sendData[3] = this.getKKFormVK(value.Keys[0].KeyCode[0])
      sendData[4] = value.DelayTime & 0xff
      sendData[5] = value.DelayTime >> 8
      await this.setMagnetKey(sendData)
    }
  }

  private async setKME(data: Map<KK, SetMagnetSenior>) {
    for (const [key, value] of data) {
      let modePolicy = 0
      let modePriority = 0
      if (value.KMEMode[0] == 0 && value.KMEMode[1] == 0) {
        modePolicy = 1 // 后按下优先
      }
      if (value.KMEMode[0] == 0 && value.KMEMode[1] == 1) {
        modePolicy = 2 // 绝对优先
      }
      if (value.KMEMode[0] == 1 && value.KMEMode[1] == 0) {
        modePolicy = 2 // 绝对优先
        modePriority = 1
      }

      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetSOCD
      sendData[1] = key
      sendData[2] = this.getKKFormVK(value.Keys[0].KeyCode[0])
      sendData[3] = modePolicy
      sendData[4] = modePriority
      await this.setMagnetKey(sendData)
    }
  }

  private async setRS(data: Map<KK, SetMagnetSenior>) {
    for (const [key, value] of data) {
      const sendData = Array(64).fill(0)
      sendData[0] = MG63SetCmd.SetRS
      sendData[1] = key
      sendData[2] = this.getKKFormVK(value.Keys[0].KeyCode[0])
      await this.setMagnetKey(sendData)
    }
  }

  private createDefaultMagnetSeniorData(type: MagnetSeniorType): SetMagnetSenior {
    return {
      KeyType: type,
      Keys: [
        { KeyCode: [0, 0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
        { KeyCode: [0, 0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
        { KeyCode: [0, 0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
        { KeyCode: [0, 0, 0], KeyFunction: [0, 0, 0, 0], Travel: 0 },
      ],
      MTDuration: 0,
      TGLEnable: 0,
      TGLMode: 0x05,
      KMEMode: [0, 0],
      DelayTime: 0,
    }
  }

  private decodeHidKeyCode(rawKey: number): [number, number, number] {
    if (rawKey === 0 || rawKey === 0xff) {
      return [0, 0, 0]
    }

    const vk = VKFromHID(rawKey)
    if (vk === undefined) {
      return [0, 0, 0]
    }

    return [vk, 0, 0]
  }

  private decodeDksKeyFunctions(keyInfo: MG63MagKeyInfo): [number, number, number, number][] {
    const [phase0 = 0, phase3 = 0, phase1 = 0, phase2 = 0] = keyInfo.dksEventConfig
    const phaseFlags = [phase0, phase1, phase2, phase3]
    // 与 setTrPSx 保持一致：HidCode i → 包含位=bit(4+i)，长按位=bit(0+i)。
    // “包含位=1 + 长按位=0” → v=1（按下）；
    // “包含位=1 + 长按位=1” → v=2 或 v=3（二者在协议层面完全等价，
    //   UI 中 v=3 仅为子段边界标记，固件不区分）。还原时统一输出 v=2，
    //   避免在 [2,2,2,0] 类单一 release 范围上虚造出 v=3。
    // 与 setTrPSx 保持一致：HidCode i → 包含位=bit(4+i)，长按位=bit(0+i)。
    // Keys[0]↔HidCode0↔bit4/bit0，Keys[3]↔HidCode3↔bit7/bit3。
    const bitMap: [number, number][] = [
      [4, 0],
      [5, 1],
      [6, 2],
      [7, 3],
    ]
    const keyFunctions = Array.from({ length: 4 }, () => [0, 0, 0, 0]) as [number, number, number, number][]

    for (let phaseIndex = 0; phaseIndex < phaseFlags.length; phaseIndex++) {
      const flagValue = phaseFlags[phaseIndex]
      for (let keyIndex = 0; keyIndex < bitMap.length; keyIndex++) {
        const [highBit, lowBit] = bitMap[keyIndex]
        const highBitValue = (flagValue >> highBit) & 0x01
        if (highBitValue === 0) {
          keyFunctions[keyIndex][phaseIndex] = 0
          continue
        }
        const lowBitValue = (flagValue >> lowBit) & 0x01
        keyFunctions[keyIndex][phaseIndex] = lowBitValue === 0 ? 1 : 2
      }
    }

    return keyFunctions
  }

  private getMatrixRowCount(): number {
    return deviceManager.deviceID === VirtualMG75V2C12 ? 6 : 5
  }

  private getPartnerKeyCodeFromMatrixPosition(row: number, col: number): [number, number, number] {
    const matrixIndex = row + col * this.getMatrixRowCount()
    const kk = getHuochairenMatrixIndexToKKMap(deviceManager.deviceID)[matrixIndex]
    if (kk === undefined || kk === 0xff) {
      return [0, 0, 0]
    }

    try {
      const vk = vkFromKK(kk as KK, deviceManager.deviceID)
      return vk !== undefined ? [vk, 0, 0] : [0, 0, 0]
    } catch (error) {
      void error
      return [0, 0, 0]
    }
  }

  async getSeniorKeyLayoutOverlay(): Promise<Map<KK, SetMagnetSenior>> {
    const overlay = new Map<KK, SetMagnetSenior>()
    const keys = allKeysForDevice(deviceManager.deviceID)
    if (keys.length === 0) {
      return overlay
    }

    const keysData = await this.getKeysTravelData()

    // 火柴人固件 Bug 适配：SOCD 配对完整性校验（仅清洗内存数据，不写回硬件）
    //validateSOCDPairs(keysData, this.getMatrixRowCount())

    const keyToMatrixIndex = huochairenMagMapReverse(deviceManager.deviceID)

    for (const key of keys) {
      const matrixIndex = keyToMatrixIndex[key]
      const keyInfo = keysData[matrixIndex]
      if (keyInfo === undefined) {
        continue
      }

      switch (keyInfo.keyMode) {
        case 2: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeDKS)
          const dksKeyFunctions = this.decodeDksKeyFunctions(keyInfo)
          const pressTravel = keyInfo.dksLowTriggerPointMm / 100
          const releaseTravel = keyInfo.dksHighTriggerPointMm / 100

          // 协议：dksKeyValues[i] 即 HidCode i，与 UI Keys[i] 一一对应。
          keyInfo.dksKeyValues.forEach((_, index) => {
            const rawKey = keyInfo.dksKeyValues[index] ?? 0
            magnetData.Keys[index].KeyCode = this.decodeHidKeyCode(rawKey)
            magnetData.Keys[index].Travel = index === 0 || index === 3 ? pressTravel : releaseTravel
          })
          dksKeyFunctions.forEach((keyFunction, index) => {
            magnetData.Keys[index].KeyFunction = keyFunction
          })

          overlay.set(key, magnetData)
          break
        }
        case 3: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeMT)
          magnetData.Keys[0].KeyCode = this.decodeHidKeyCode(keyInfo.mtHoldKey)
          magnetData.Keys[1].KeyCode = this.decodeHidKeyCode(keyInfo.mtTapKey)
          magnetData.MTDuration = Math.round(keyInfo.mtHoldTime / 10)
          overlay.set(key, magnetData)
          break
        }
        case 4: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeTGL)
          magnetData.Keys[0].KeyCode = this.decodeHidKeyCode(keyInfo.tglKey)
          magnetData.TGLMode = keyInfo.tglMode === 1 ? 0x05 : 0x04
          overlay.set(key, magnetData)
          break
        }
        case 5: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeKME)
          // 火柴人固件 SOCD 的 partnerRow/partnerCol 与驱动矩阵约定相反，需交换
          magnetData.Keys[0].KeyCode = this.getPartnerKeyCodeFromMatrixPosition(keyInfo.socdPartnerCol, keyInfo.socdPartnerRow)
          if (keyInfo.socdPolicy === 1) {
            magnetData.KMEMode = [0, 0]
          } else if (keyInfo.socdPolicy === 2 && keyInfo.socdPriority === 0) {
            magnetData.KMEMode = [0, 1]
          } else if (keyInfo.socdPolicy === 2 && keyInfo.socdPriority === 1) {
            magnetData.KMEMode = [1, 0]
          } else {
            magnetData.KMEMode = [1, 1]
          }
          overlay.set(key, magnetData)
          break
        }
        case 6: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeRS)
          // 火柴人固件 RS 的 partnerRow/partnerCol 与驱动矩阵约定相反，需交换
          magnetData.Keys[0].KeyCode = this.getPartnerKeyCodeFromMatrixPosition(keyInfo.rsPartnerCol, keyInfo.rsPartnerRow)
          overlay.set(key, magnetData)
          break
        }
        case 7: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeEND)
          magnetData.Keys[0].KeyCode = this.decodeHidKeyCode(keyInfo.endReleaseKey || keyInfo.endPressKey)
          magnetData.DelayTime = keyInfo.endDelayTime as 0
          overlay.set(key, magnetData)
          break
        }
        case 8: {
          const magnetData = this.createDefaultMagnetSeniorData(MagnetSeniorType.SeniorKeyTypeMPT)
          magnetData.Keys[0].KeyCode = this.decodeHidKeyCode(keyInfo.mptLowKey)
          magnetData.Keys[0].Travel = keyInfo.mptLowTriggerPointMm
          magnetData.Keys[1].KeyCode = this.decodeHidKeyCode(keyInfo.mptMidKey)
          magnetData.Keys[1].Travel = keyInfo.mptMidTriggerPointMm
          magnetData.Keys[2].KeyCode = this.decodeHidKeyCode(keyInfo.mptHighKey)
          magnetData.Keys[2].Travel = keyInfo.mptHighTriggerPointMm
          overlay.set(key, magnetData)
          break
        }
      }
    }

    return overlay
  }
  async syncSeniorkey(): Promise<void> {
    await this.parent.musicIns.musicCapturePause()
    const dksData = new Map<KK, SetMagnetSenior>()
    const mtData = new Map<KK, SetMagnetSenior>()
    const tglData = new Map<KK, SetMagnetSenior>()
    const mptData = new Map<KK, SetMagnetSenior>()
    const endData = new Map<KK, SetMagnetSenior>()
    const kmeData = new Map<KK, SetMagnetSenior>()
    const rsData = new Map<KK, SetMagnetSenior>()

    for (let index = 0; index < this.parent.save.Keys.length; index++) {
      const value = this.parent.save.Keys[index]
      if (value.Output.Mode != OutputMode.Magnet) continue

      const magnetData = this.parent.save.MagnetData.get(value.Output.DataIndex)
      if (magnetData == undefined) continue
      if (magnetData.KeyType == MagnetSeniorType.SeniorKeyTypeNone) continue
      switch (magnetData.KeyType) {
        case MagnetSeniorType.SeniorKeyTypeDKS:
          dksData.set(value.Input.KK, magnetData)
          break
        case MagnetSeniorType.SeniorKeyTypeMT:
          mtData.set(value.Input.KK, magnetData)
          break
        case MagnetSeniorType.SeniorKeyTypeTGL:
          tglData.set(value.Input.KK, magnetData)
          break
        case MagnetSeniorType.SeniorKeyTypeMPT:
          mptData.set(value.Input.KK, magnetData)
          break
        case MagnetSeniorType.SeniorKeyTypeEND:
          endData.set(value.Input.KK, magnetData)
          break
        case MagnetSeniorType.SeniorKeyTypeKME:
          kmeData.set(value.Input.KK, magnetData)
          break
        case MagnetSeniorType.SeniorKeyTypeRS:
          rsData.set(value.Input.KK, magnetData)
          break
      }
    }
    await this.clearKME()
    if (dksData.size > 0) {
      await this.setDKS(dksData)
    }
    if (mtData.size > 0) {
      await this.setMT(mtData)
    }
    if (tglData.size > 0) {
      await this.setTGL(tglData)
    }
    if (mptData.size > 0) {
      await this.setMPT(mptData)
    }
    if (endData.size > 0) {
      await this.setEND(endData)
    }
    if (kmeData.size > 0) {
      await this.setKME(kmeData)
    }
    if (rsData.size > 0) {
      await this.setRS(rsData)
    }

    await this.parent.musicIns.musicCaptureContinue()
  }

  async clearKME() {
    const keys = this.parent.resetKmeKeys
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const data = Array(64).fill(0)
      data[0] = 0x20
      data[2] = key.Input.KK
      data[3] = key.Input.KK
      await this.DeviceHID.send(data)
    }
    this.parent.clearKmeKeys()
  }

  // async clearKME(): Promise<void> {
  //   const keys = this.parent.resetKmeKeys
  //   for (let index = 0; index < keys.length; index++) {
  //     const key = keys[index]
  //     await this.parent.delHigherKeyMode(key.Input.KK)
  //   }
  //   this.parent.clearKmeKeys()
  // }

  private async init() {
    await this.sendEndTravels()
    await this.setKeyTravelCalibrationMode(false)
    await delay(20)
  }

  private keysTravelDataCache: MG63MagKeyInfo[] = []
  private cacheTimeout?: ReturnType<typeof setTimeout>

  async getKeysTravelData(): Promise<MG63MagKeyInfo[]> {
    await this.initPromise

    const useV118Protocol = await this.isDeadZoneProtocolV118()

    if (this.keysTravelDataCache.length > 0) {
      return this.keysTravelDataCache
    }
    if (this.fetchPromise) {
      return this.fetchPromise
    }

    this.fetchPromise = (async () => {
      this.parent.isBusy = true
      try {
        const state = this.parent.save.ColorsDynamic.Run
        return await this.parent.withMusicCapturePausedForRead(async () => {
          if (state === true) {
            await delay(200)
          }
          // 158 是最大值
          // MG75V2 有 158 个有效负载包 (每个有效负载 60 字节，包含多个按键信息)
          // MG63/RA68 通常由于按键更少，这里旧代码对应是 94 (158 是最大值，可以兼容小型设备)
          const isMG75V2 = deviceManager.deviceID === VirtualMG75V2C12
          const packetCount = isMG75V2 ? 110 : 94

          // 优化 1: 预分配 Uint8Array，避免 ...spread 和大量切片
          // 每次请求有效负载 60 字节 (64字节响应 - 4字节头部)
          const totalRawData = new Uint8Array(packetCount * 60)
          const sendData = Array(64).fill(0)
          sendData[0] = MG63SetCmd.ReadData
          sendData[1] = this.parent.getCurrentProfileIndex()

          // 读取并校验，固件来不及准备数据时会返回脏内存（U16 值超出物理行程），最多重试 3 次
          const MAX_READ_RETRIES = 3
          const READ_RETRY_DELAY = 150 // ms
          const infoSize = useV118Protocol ? MG63_MAG_KEY_INFO_SIZE_V118 : MG63_MAG_KEY_INFO_SIZE_LEGACY

          let result: MG63MagKeyInfo[] = []
          let readSuccess = false

          for (let attempt = 0; attempt < MAX_READ_RETRIES; attempt++) {
            if (attempt > 0) {
              console.warn(`MG63Magnet: travel data invalid, retry ${attempt}/${MAX_READ_RETRIES - 1}`)
              await delay(READ_RETRY_DELAY)
            }

            totalRawData.fill(0)
            for (let i = 0; i < packetCount; i++) {
              sendData[2] = i & 0xff
              sendData[3] = (i >> 8) & 0xff
              const response = await this.parent.send(sendData)
              // 仅在长度符合预期时填充
              if (response.length >= 64) {
                totalRawData.set(response.slice(4), i * 60)
              }
            }

            // 计算确切能够组成的完整按键信息数量
            const totalInfos = Math.floor(totalRawData.length / infoSize)
            const parsed: MG63MagKeyInfo[] = []
            for (let i = 0; i < totalInfos; i++) {
              const start = i * infoSize
              const packet = totalRawData.subarray(start, start + infoSize)
              parsed.push(decodeMagKeyInfo(packet, useV118Protocol))
            }

            // 校验：任意按键行程超出物理上限则认为本次读取数据无效
            // RT 字段仅在 isRTEnabled 时有意义，未启用时固件该字段为脏内存，不参与校验
            const hasInvalidData = parsed.some(
              (info) =>
                info.pressTriggerPointMm > MG63_MAX_VALID_TRAVEL ||
                info.releaseTriggerPointMm > MG63_MAX_VALID_TRAVEL ||
                (info.isRTEnabled && info.rtPressTriggerPointMm > MG63_MAX_VALID_TRAVEL) ||
                (info.isRTEnabled && info.rtReleaseTriggerPointMm > MG63_MAX_VALID_TRAVEL),
            )

            if (!hasInvalidData) {
              result = parsed
              readSuccess = true
              break
            }

            result = parsed // 保留最后一次结果，重试耗尽时使用
          }

          if (!readSuccess) {
            console.error('MG63Magnet: travel data still invalid after retries, using last read result')
          }

          this.keysTravelDataCache = result

          // 优化 3: 这里的缓存清除逻辑
          if (this.cacheTimeout) clearTimeout(this.cacheTimeout)
          this.cacheTimeout = setTimeout(() => {
            this.keysTravelDataCache = []
          }, 2000)

          if (state === true) {
            await delay(200)
          }
          return result
        })
      } finally {
        this.parent.isBusy = false
        this.fetchPromise = null
      }
    })()

    return this.fetchPromise
  }
  /*
   * @param packetSize - 每包大小，默认 75
   * @returns 分包后的二维数组
   */
  private splitIntoPackets(data: number[], packetSize: number = 75): number[][] {
    if (packetSize <= 0) {
      throw new Error('packetSize must be greater than 0')
    }

    const packets: number[][] = []
    for (let i = 0; i < data.length; i += packetSize) {
      const chunk = data.slice(i, i + packetSize)
      packets.push(chunk)
    }
    return packets
  }
  async getKeysTravels(keys?: KK[]): Promise<postMagnetKeyInfo[]> {
    let res: postMagnetKeyInfo[] = []
    if (!keys) {
      keys = allKeysForDevice(deviceManager.deviceID)
    }

    // // 旧协议固件（< 1.20）：CMD 0x40 写入运行时内存，CMD 0xe0 读回 profile 存储区出厂默认值。
    // // 跳过设备读取，直接从本地 save 返回已设置的行程数据，避免切换 Tab 后数据恢复为默认值。
    // if (!this.parent.supportsOnboardConfigs()) {
    //   const magnetTravel = this.parent.save.MagnetTravel
    //   keys.forEach((key) => {
    //     const travelInfo = magnetTravel.get(key)
    //     res.push({
    //       Key: key,
    //       KeyType: 0,
    //       PressNum: travelInfo?.PressNum ?? 0,
    //       ReleaseNum: travelInfo?.ReleaseNum ?? 0,
    //       QuickTriggerPressNum: travelInfo?.QuickTriggerPressNum ?? 0,
    //       QuickTriggerReleaseNum: travelInfo?.QuickTriggerReleaseNum ?? 0,
    //     })
    //   })
    //   return res
    // }

    const keysData = await this.getKeysTravelData()
    const keyMagMapReverse = huochairenMagMapReverse(deviceManager.deviceID)

    // keysData.forEach((key, index) => {
    //   if (key.keyMode > 0 && key.releaseTriggerPointMm > 0) {
    //     console.log('keymode', index.toString(16), key)
    //   }
    //   if (key.dksKeyValues.filter((v) => v > 0).length > 0) {
    //     // console.log('dksKeyValues', index, key)
    //   }
    // })

    keys.forEach((key) => {
      const index = keyMagMapReverse[key]
      // if (key == 0x46) {
      //   console.log('keyInfo', index)
      // }
      // console.log('index', index)
      const keyInfo = keysData[index]

      if (!keyInfo) {
        console.warn(`MG63Magnet: No key data found for key ${key.toString(16)} at index ${index}`)
        return
      }
      const one = {
        Key: key,
        KeyType: huochairenMagnetModeConvert(keyInfo.keyMode),
        PressNum: keyInfo.pressTriggerPointMm,
        ReleaseNum: keyInfo.releaseTriggerPointMm,
        QuickTriggerPressNum: keyInfo.isRTEnabled ? keyInfo.rtPressTriggerPointMm : 0,
        QuickTriggerReleaseNum: keyInfo.isRTEnabled ? keyInfo.rtReleaseTriggerPointMm : 0,
      }
      res.push(one)
    })
    return res
  }

  async readOrSetDeadZone(_isWrite: boolean, _value?: [number, number]): Promise<[number, number]> {
    return [0, 0]
  }

  async getKeysDeadZone(keys?: KK[]): Promise<Map<KK, DataDeadZone>> {
    if (!keys) {
      keys = allKeysForDevice(deviceManager.deviceID)
    }
    const res = new Map<KK, DataDeadZone>()

    // 旧协议固件（< 1.20）：直接从本地 save 返回死区数据，跳过设备读取。
    if (!this.parent.supportsOnboardConfigs()) {
      const deadZoneList = this.parent.save.DeadZoneList
      keys.forEach((key) => {
        const dz = deadZoneList.get(key)
        if (dz) res.set(key, { Top: dz.Top, Bottom: dz.Bottom })
      })
      return res
    }

    const keysData = await this.getKeysTravelData()
    const keyMagMapReverse = huochairenMagMapReverse(deviceManager.deviceID)
    console.log('getKeysDeadZone', keysData)
    keys.forEach((key) => {
      const index = keyMagMapReverse[key]
      const keyInfo = keysData[index]
      if (keyInfo) {
        // 死区数据在驱动内部统一保持为 0.001mm，直接回填给上层 UI/配置。
        const one = {
          Key: key,
          Top: keyInfo.topDeadZone,
          Bottom: keyInfo.bottomDeadZone,
        }
        res.set(one.Key, one)
      } else {
        console.warn(`MG63Magnet: No deadzone data found for key ${key} at index ${index}`)
      }
    })
    // console.log('getKeysDeadZone', res)
    return res
  }

  private async isDeadZoneProtocolV118(): Promise<boolean> {
    if (this.deadZoneProtocolV118Promise == null) {
      // 火柴人设备最低固件版本为 1.20（新协议 byte63=1），必然 >= 1.18；
      // 旧固件（byte63=0）通过版本字符串兜底判断。
      this.deadZoneProtocolV118Promise = this.parent
        .getVersionInfo()
        .then(({ version, isNewProtocol }) => isNewProtocol || version >= '1.18')
        .catch((error) => {
          console.warn('MG63Magnet: failed to get firmware version, fallback to legacy deadzone protocol', error)
          return false
        })
    }

    return this.deadZoneProtocolV118Promise
  }

  async syncKeysTravels(): Promise<void> {
    await this.parent.musicIns.musicCapturePause()
    //const keys = allKeysForDevice(deviceManager.deviceID)
    const travels = this.parent.save.MagnetTravel

    const setData = new Map<KK, number[]>()

    const rtCloseSetData = new Map<KK, number[]>() // 关闭RT
    const rtReleaseSetData = new Map<KK, number[]>() // 释放RT
    const rtContinuousTriggeringData = new Map<KK, number[]>() // 连续触发RT

    for (const [key, value] of travels) {
      setData.set(key, [value.PressNum & 0xff, (value.PressNum >> 8) & 0xff])
      if (value.QuickTriggerPressNum == 0 && value.QuickTriggerReleaseNum == 0) {
        rtCloseSetData.set(key, [0, 0, 0, 0])
      }
      if (value.QuickTriggerPressNum == 0 && value.QuickTriggerReleaseNum > 0) {
        rtReleaseSetData.set(key, [value.QuickTriggerPressNum & 0xff, (value.QuickTriggerPressNum >> 8) & 0xff, value.QuickTriggerReleaseNum & 0xff, (value.QuickTriggerReleaseNum >> 8) & 0xff])
      }
      if (value.QuickTriggerPressNum > 0 && value.QuickTriggerReleaseNum > 0) {
        rtContinuousTriggeringData.set(key, [value.QuickTriggerPressNum & 0xff, (value.QuickTriggerPressNum >> 8) & 0xff, value.QuickTriggerReleaseNum & 0xff, (value.QuickTriggerReleaseNum >> 8) & 0xff])
      }
    }
    await this.parent.cmdSet([MG63SetCmd.SetTravel, 0], setData, 150)
    if (rtCloseSetData.size > 0) {
      await this.parent.cmdSet([MG63SetCmd.SetRTTravel, 0, 0, 0], rtCloseSetData, 150)
    }
    if (rtReleaseSetData.size > 0) {
      await this.parent.cmdSet([MG63SetCmd.SetRTTravel, 0, 0, 1], rtReleaseSetData, 150)
    }
    if (rtContinuousTriggeringData.size > 0) {
      await this.parent.cmdSet([MG63SetCmd.SetRTTravel, 0, 0, 1], rtContinuousTriggeringData, 150)
    }
    await this.parent.musicIns.musicCaptureContinue()
  }

  async syncKeysDeadZone(): Promise<void> {
    await this.parent.musicIns.musicCapturePause()
    try {
      const deadZoneList = this.parent.save.DeadZoneList
      const deadZoneSetData = new Map<KK, number[]>()
      const useV118Protocol = await this.isDeadZoneProtocolV118()
      for (const [key, value] of deadZoneList) {
        if (useV118Protocol) {
          // 1.18 及以上：key + top(U16 LE, 0.001mm) + bottom(U16 LE, 0.001mm)
          deadZoneSetData.set(key, [value.Top & 0xff, (value.Top >> 8) & 0xff, value.Bottom & 0xff, (value.Bottom >> 8) & 0xff])
        } else {
          // 1.18 以下：key + top(U8, 0.01) + bottom(U8,0.01)
          deadZoneSetData.set(key, [value.Top, value.Bottom])
        }
      }
      await this.parent.cmdSet([MG63SetCmd.SetDeadZone, 0], deadZoneSetData)
    } finally {
      await this.parent.musicIns.musicCaptureContinue()
    }
  }
  async DemoDebug(): Promise<void> {}
  destroy() {
    this.calibrationSate = false
    if (this.calibrationTimer) {
      clearInterval(this.calibrationTimer)
      this.calibrationTimer = undefined
    }
  }
}
